import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from './LoadingSpinner';
import ProfilePicture from './ProfilePicture';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { AUTH_BASE, API_BASE } from '../config';

function UserProfile({ 
  isOpen, 
  onClose, 
  user, 
  targetUserId = null, // If provided, shows another user's profile (read-only)
  onProfileUpdate = null // Callback when current user's profile is updated
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    role: 'Student',
    campus: 'Amherst',
    bio: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [uploadingPic, setUploadingPic] = useState(false);
  const [picPreview, setPicPreview] = useState(null);
  const picInputRef = useRef(null);

  const isOwnProfile = !targetUserId || targetUserId === user?.uid;

  useEffect(() => {
    if (isOpen) {
      console.log('[UserProfile] Modal opened. isOwnProfile:', isOwnProfile, 'targetUserId:', targetUserId, 'user:', user);
      if (isOwnProfile) {
        // Load current user's profile
        setProfile({
          displayName: user?.displayName || '',
          email: user?.email || '',
          role: user?.role || 'Student',
          campus: user?.campus || 'Amherst',
          bio: user?.bio || ''
        });
      } else {
        // Load target user's profile
        fetchUserProfile(targetUserId); //targetUserId is the uid of the user we are viewing
      }
    }
  }, [isOpen, targetUserId, user, isOwnProfile]);

  const fetchUserProfile = async (uid) => {
    setLoading(true);
    setError('');
    console.log('[UserProfile] Fetching seller profile for uid:', uid);
    try {
      const response = await fetch(`${AUTH_BASE}/profile/${uid}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }
      
      const profileData = await response.json();
      setProfile({
        displayName: profileData.displayName || '',
        email: profileData.email || 'UMass Student', // Don't show other users' emails
        role: profileData.role || 'Student',
        campus: profileData.campus || 'Amherst',
        bio: profileData.bio || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // List of allowed test emails or domains
  const allowedTestEmails = [
    'himnishchhabra@gmail.com','kinghc1st@gmail.com','shilinchhabra1@gmail.com'
  ];
  
  const allowedTestDomains = [
    'example.com',
    'test.com',
    'gmail.com'
  ];

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const isAllowedEmail = (email) => {
    if (email.endsWith('@umass.edu')) return true;
    
    // Check if it's in the allowed test emails list
    if (allowedTestEmails.includes(email.toLowerCase())) return true;
    
    // Check if the domain is allowed
    for (const domain of allowedTestDomains) {
      if (email.toLowerCase().endsWith(`@${domain}`)) return true;
    }
    
    return false;
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setProfile({...profile, email: newEmail});
    
    if (!newEmail) {
      setEmailError('Email is required');
    } else if (!validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else if (!isAllowedEmail(newEmail)) {
      setEmailError('Only @umass.edu or approved test emails are allowed');
    } else {
      setEmailError('');
    }
  };

  const handleSave = async () => {
    // Validate email before saving
    if (!profile.email) {
      setEmailError('Email is required');
      return;
    } else if (!validateEmail(profile.email)) {
      setEmailError('Please enter a valid email address');
      return;
    } else if (!isAllowedEmail(profile.email)) {
      setEmailError('Only @umass.edu or approved test emails are allowed');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${AUTH_BASE}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          role: profile.role.toLowerCase(),
          campus: profile.campus,
          bio: profile.bio
        })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to update profile');
      }

      const updatedProfile = await response.json();

      // Update the profile state with the response
      setProfile(prev => ({
        ...prev,
        role: updatedProfile.role || prev.role,
        campus: updatedProfile.campus,
        bio: updatedProfile.bio
      }));

      // Notify parent component of the update
      if (onProfileUpdate) {
        onProfileUpdate(updatedProfile);
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setPicPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('images', file);
      const uploadRes = await fetch(`${API_BASE}/media/upload/images`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const { data } = await uploadRes.json();
      const url = data?.[0]?.url;
      if (!url) throw new Error('No URL returned');

      // Persist to user profile
      const saveRes = await fetch(`${AUTH_BASE}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ profilePicture: url }),
      });
      if (!saveRes.ok) throw new Error('Failed to save profile picture');
      const updated = await saveRes.json();
      if (onProfileUpdate) onProfileUpdate(updated);
    } catch (err) {
      console.error('Profile picture upload error:', err);
      setPicPreview(null);
      setError('Could not upload profile picture. Please try again.');
    } finally {
      setUploadingPic(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    if (isOwnProfile) {
      setProfile({
        displayName: user?.displayName || '',
        email: user?.email || '',
        role: user?.role || 'Student',
        campus: user?.campus || 'Amherst',
        bio: user?.bio || ''
      });
    }
    setIsEditing(false);
    setEmailError('');
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{transform:translateY(30px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        animation: 'slideUp 0.3s ease-out'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(0,0,0,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            fontSize: '1.5rem',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
          onMouseOver={(e)=>e.currentTarget.style.background='rgba(0,0,0,0.2)'}
          onMouseOut={(e)=>e.currentTarget.style.background='rgba(0,0,0,0.1)'}
        >
          ×
        </button>

        {/* Header */}
        <div style={{
          padding: '2rem 2rem 1rem 2rem',
          borderBottom: '1px solid #eee'
        }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            color: 'var(--maroon)',
            fontSize: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {isOwnProfile ? 'Your Profile' : `${profile.displayName}'s Profile`}
          </h2>
          {isOwnProfile && !isEditing && (
            <p style={{ margin: 0, color: 'var(--text-soft)', fontSize: '0.9rem' }}>
              Manage your profile information
            </p>
          )}
        </div>

        {/* Content */}
        <div style={{ 
          padding: '1.5rem 2rem 2rem 2rem',
          fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        }}>
          {loading ? (
            <div style={{ padding:'2rem 0', textAlign:'center' }}>
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#d32f2f' }}>{error}</p>
            </div>
          ) : (
            <div>
              {/* Profile Picture */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <ProfilePicture 
                    src={picPreview || user?.profilePicture}
                    alt={profile.displayName}
                    size="large"
                    fallbackColor="var(--maroon)"
                  />
                  {uploadingPic && (
                    <div style={{
                      position: 'absolute', inset: 0, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.45)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', border: '3px solid #fff', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
                    </div>
                  )}
                </div>

                {isOwnProfile && (
                  <>
                    <input
                      ref={picInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleProfilePicUpload}
                    />
                    <button
                      onClick={() => picInputRef.current?.click()}
                      disabled={uploadingPic}
                      style={{
                        marginTop: '0.6rem', padding: '5px 14px',
                        background: 'transparent', border: '1.5px solid var(--maroon)',
                        borderRadius: 20, color: 'var(--maroon)', cursor: 'pointer',
                        fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit',
                      }}
                    >
                      {uploadingPic ? 'Uploading…' : 'Change Photo'}
                    </button>
                  </>
                )}
              </div>

              {/* Profile Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Name */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                    Name
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    {profile.displayName}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                    Email
                  </label>
                  {isEditing && isOwnProfile ? (
                    <TextField
                      type="email"
                      value={profile.email}
                      onChange={handleEmailChange}
                      placeholder="your.email@umass.edu"
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={!!emailError}
                      helperText={emailError}
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: emailError ? '#dc3545' : '#e4e3e2' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: emailError ? '#dc3545' : 'var(--maroon)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: emailError ? '#dc3545' : 'var(--maroon)' }
                      }}
                    />
                  ) : (
                    <div style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {profile.email}
                    </div>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                    Role
                  </label>
                  {isEditing ? (
                    <FormControl fullWidth size="small">
                      <InputLabel id="role-label" sx={{ display: 'none' }}>Role</InputLabel>
                      <Select
                        labelId="role-label"
                        value={profile.role}
                        onChange={(e) => setProfile({ ...profile, role: e.target.value })}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--maroon)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--maroon)' }
                        }}
                        MenuProps={{ disablePortal: true }}
                      >
                        <MenuItem value="Student">Student</MenuItem>
                        <MenuItem value="Faculty">Faculty</MenuItem>
                        <MenuItem value="Employee">Employee</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <div style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {profile.role}
                    </div>
                  )}
                </div>

                {/* Campus */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                    Campus
                  </label>
                  {isEditing ? (
                    <FormControl fullWidth size="small">
                      <InputLabel id="campus-label" sx={{ display: 'none' }}>Campus</InputLabel>
                      <Select
                        labelId="campus-label"
                        value={profile.campus}
                        onChange={(e) => setProfile({ ...profile, campus: e.target.value })}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--maroon)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--maroon)' }
                        }}
                        MenuProps={{ disablePortal: true }}
                      >
                        <MenuItem value="Amherst">Amherst</MenuItem>
                        <MenuItem value="Boston">Boston</MenuItem>
                        <MenuItem value="Lowell">Lowell</MenuItem>
                        <MenuItem value="Mt Ida">Mt Ida</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <div style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      {profile.campus}
                    </div>
                  )}
                </div>

                {/* Bio */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                    Bio
                  </label>
                  {isEditing ? (
                    <TextField
                      value={profile.bio}
                      onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                      placeholder="Tell others about yourself..."
                      variant="outlined"
                      fullWidth
                      size="small"
                      multiline
                      minRows={4}
                      inputProps={{ maxLength: 500 }}
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e3e2' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--maroon)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--maroon)' }
                      }}
                    />
                  ) : (
                    <div style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9',
                      minHeight: '100px',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {profile.bio || 'No bio provided'}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {isOwnProfile && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '1rem',
                  marginTop: '2rem'
                }}>
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: 'transparent',
                          color: 'var(--text-soft)',
                          border: '2px solid #e4e3e2',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          minWidth: '120px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#f0efeb';
                          e.target.style.borderColor = 'var(--text-soft)';
                          e.target.style.color = 'var(--text-muted)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#e4e3e2';
                          e.target.style.color = 'var(--text-soft)';
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: 'var(--maroon)',
                          color: 'white',
                          border: '2px solid var(--maroon)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          minWidth: '120px',
                          boxShadow: '0 2px 4px rgba(136, 28, 28, 0.2)'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = 'var(--maroon-hover)';
                          e.target.style.borderColor = 'var(--maroon-hover)';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(136, 28, 28, 0.3)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'var(--maroon)';
                          e.target.style.borderColor = 'var(--maroon)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 4px rgba(136, 28, 28, 0.2)';
                        }}
                      >
                        Save Changes
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: 'var(--maroon)',
                        color: 'white',
                        border: '2px solid var(--maroon)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        minWidth: '120px',
                        boxShadow: '0 2px 4px rgba(136, 28, 28, 0.2)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = 'var(--maroon-hover)';
                        e.target.style.borderColor = 'var(--maroon-hover)';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(136, 28, 28, 0.3)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'var(--maroon)';
                        e.target.style.borderColor = 'var(--maroon)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(136, 28, 28, 0.2)';
                      }}
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserProfile; 